import { useWatchField } from '@framework/form/hooks/use-watch-field/useWatchField.tsx';
import { CheckboxInput } from '@framework/form/inputs/CheckboxInput.tsx';
import { RowElement } from '@framework/ui/components/row/RowElement.tsx';
import { Space, Tooltip } from 'antd';
import { useCallback } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ConfigFormItem } from '../../../../../ui/form/ConfigFormItem.tsx';
import { useWidgetVisibility } from '../../../hooks/useWidgetVisibility.ts';
import { type IWidgetComponentProps } from '../../../IWidgetType.ts';
import { getWidgetProps } from '../../inputs/common/util/getWidgetProps.ts';
import { HiddenPaymentRowField } from './components/computed/HiddenPaymentRowField.tsx';
import { PaymentRowFieldInput } from './components/PaymentRowFieldInput.tsx';
import { PaymentRowReportField } from './components/PaymentRowReportField.tsx';
import { PaymentRowWidgetContainer } from './components/PaymentRowWidgetContainer.tsx';
import {
  getPaymentRowKey,
  type PaymentRowFieldKey,
} from './createPaymentRowFormSchema.ts';
import { type IPaymentRowWidget } from './IPaymentRowWidget.ts';
import { usePaymentRowReport } from './usePaymentRowReport.ts';

// export const PaymentRowWidgetContainer = styled.div`
//   border: 1px solid ${props => props.theme.colorBorder};
//   border-radius: ${props => props.theme.borderRadius}px;
//   padding: 8px;
//   width: 100%;
// `;

export interface IPaymentRowWidgetComponentProps
  extends IWidgetComponentProps<IPaymentRowWidget> {}

export function PaymentRowWidgetComponent(
  props: IPaymentRowWidgetComponentProps,
) {
  const { widget } = props;

  const options = getWidgetProps(props);

  const visible = useWidgetVisibility(props.widget, options);

  const methods = useForm();

  const { t } = useTranslation();

  methods.register(options.name);

  // Utility per costruire il nome di un campo
  const fieldName = useCallback(
    (key: PaymentRowFieldKey) => getPaymentRowKey(options.name, key),
    [options.name],
  );

  // Dati necessari per il calcolo del report
  const baseUnitAmount = useWatch({ name: fieldName('baseUnitAmount') });
  const quantity = useWatch({
    name: fieldName('quantity'),
  });
  const vat = useWatch({ name: fieldName('vat') });
  const discount = useWatch({
    name: fieldName('discount'),
  });

  const included = useWatchField({ name: fieldName('included') }) as boolean;

  const { taxable, total, vatAmount, unitAmount, totalNoDiscount } =
    usePaymentRowReport({
      baseUnitAmount,
      quantity,
      vat,
      discount,
    });

  if (!visible) return null;

  return (
    <PaymentRowWidgetContainer
      included={included}
      canBeExcluded={widget.canBeExcluded}
      left={
        <Tooltip
          title={t('payment-row-widget.title', 'Includi nel pagamento')}
          align={{ offset: [-12, 9] }}
          placement="topLeft"
        >
          <ConfigFormItem
            name={fieldName('included')}
            readonly={options.readonly}
            widget={props.widget}
          >
            <CheckboxInput
              name={fieldName('included')}
              readOnly={options.readonly}
            />
          </ConfigFormItem>
        </Tooltip>
      }
      report={
        widget.showReport && (
          <Space size={0} wrap>
            <PaymentRowReportField
              name={fieldName('unitAmount')}
              readonly={options.readonly}
              label={t('payment-row-widget.unit-amount', 'Importo Unitario')}
              value={unitAmount}
              widget={widget}
            />
            <PaymentRowReportField
              name={fieldName('taxableAmount')}
              readonly={options.readonly}
              label={t('payment-row-widget.taxable-amount', 'Imponibile')}
              value={taxable}
              widget={widget}
            />
            <PaymentRowReportField
              name={fieldName('vatAmount')}
              readonly={options.readonly}
              label={t('payment-row-widget.vat-amount', 'IVA')}
              value={vatAmount}
              widget={widget}
            />
            <PaymentRowReportField
              name={options.name}
              readonly={options.readonly}
              label={t('payment-row-widget.total', 'Totale')}
              value={total}
              widget={widget}
            />
          </Space>
        )
      }
    >
      <PaymentRowFieldInput
        name={options.name}
        field="description"
        config={widget.description}
        label={t('payment-row-widget.description', 'Descrizione')}
        type="string"
        options={options}
        widget={widget}
      />
      <RowElement>
        <PaymentRowFieldInput
          name={options.name}
          field="baseUnitAmount"
          config={widget.baseUnitAmount}
          label={t('payment-row-widget.base-unit-amount', 'Importo Listino')}
          type="currency"
          options={options}
          widget={widget}
        />
        <PaymentRowFieldInput
          name={options.name}
          field="quantity"
          config={widget.quantity}
          label={t('payment-row-widget.quantity', 'Quantità')}
          type="number"
          options={options}
          widget={widget}
        />
        <PaymentRowFieldInput
          name={options.name}
          field="unit"
          config={widget.unit}
          label={t('payment-row-widget.unit', 'Unità di Misura')}
          type="string"
          options={options}
          widget={widget}
        />
        <PaymentRowFieldInput
          name={options.name}
          field="vat"
          config={widget.vat}
          label={t('payment-row-widget.vat', 'IVA')}
          type="percentage"
          options={options}
          widget={widget}
        />
        <PaymentRowFieldInput
          name={options.name}
          field="discount"
          config={widget.discount}
          label={t('payment-row-widget.discount', 'Sconto/Esenzione')}
          type="percentage"
          options={options}
          widget={widget}
        />
      </RowElement>
      <HiddenPaymentRowField
        name={fieldName('totalNoDiscountAmount')}
        readonly={options.readonly}
        value={totalNoDiscount}
      />
    </PaymentRowWidgetContainer>
  );
}
